import '../App.css'
import { Image, Text, Container, Space, createStyles, rem, Group, Divider, Badge } from '@mantine/core'
import Footer from '../components/Footer';
import Head from '../components/Head';
import { IconUser, IconMapPin } from '@tabler/icons-react'
import profile from '../290591517_10109352142199085_172794176054360776_n.jpg'

const useStyles = createStyles((theme) => ({

	heroWrapper: {
		background: 'radial-gradient(ellipse at 50% 60%, #ede0ff 0%, #e8f6fb 50%, #f4fbff 100%)',
		paddingTop: rem(32),
		paddingBottom: rem(36),
		textAlign: 'center',
	},

	pageTitle: {
		fontFamily: "'Merriweather', 'Georgia', serif",
		fontWeight: 900,
		letterSpacing: '-0.01em',
		fontSize: rem(60),
		color: '#5c0099',

		[theme.fn.smallerThan('md')]: { fontSize: rem(44) },
		[theme.fn.smallerThan('sm')]: { fontSize: rem(34) },
	},

	// ── Two-column founder layout ─────────────────────────────────────────
	founderRow: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'flex-start',
		gap: rem(40),

		[theme.fn.smallerThan('md')]: {
			flexDirection: 'column',
			alignItems: 'center',
		},
	},

	photoCol: {
		flexShrink: 0,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
	},

	profileRing: {
		borderRadius: '50%',
		border: `4px solid #7b00b8`,
		boxShadow: '0 8px 32px rgba(91,0,153,0.20)',
		overflow: 'hidden',
		width: rem(220),
		height: rem(220),

		[theme.fn.smallerThan('sm')]: {
			width: rem(160),
			height: rem(160),
		},
	},

	bioCol: {
		flex: 1,
	},

	// ── Pull quote ────────────────────────────────────────────────────────
	pullQuote: {
		borderLeft: `4px solid #7b00b8`,
		paddingLeft: rem(16),
		margin: `${rem(16)} 0`,
		fontStyle: 'italic',
		color: '#532d86',
		fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
		fontWeight: 600,
		fontSize: rem(18),
		lineHeight: 1.5,

		[theme.fn.smallerThan('sm')]: { fontSize: rem(16) },
	},

	// ── IRL callout card ──────────────────────────────────────────────────
	irlCard: {
		background: 'linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 100%)',
		border: `1.5px solid #2b9eb3`,
		borderRadius: theme.radius.lg,
		padding: `${rem(20)} ${rem(24)}`,
		maxWidth: rem(560),
		margin: `${rem(24)} auto 0`,
	},

	dividerLabel: {
		fontFamily: "'Outfit', 'Helvetica Neue', sans-serif",
		fontWeight: 700,
		fontSize: rem(12),
		letterSpacing: '0.08em',
		textTransform: 'uppercase' as const,
		color: theme.colors.gray[5],
	},
}))

function About() {
	const { classes } = useStyles()

	const links = [
		{ "link": "/About", "label": "About" },
		{ "link": "/Support", "label": "Support" },
		{ "link": "/Pp", "label": "Privacy" },
		{ "link": "/Tos", "label": "Terms of Use" },
		{ "link": "/Changelog", "label": "Changelog" },
	]

	return (
		<body>
			<Head />

			{/* ── Hero ── */}
			<div className={classes.heroWrapper}>
				<Group position="center" spacing="xs" mb={rem(8)}>
					<IconUser size={44} color="#7b00b8" />
				</Group>
				<Text className={classes.pageTitle}>About</Text>
				<Group position="center" mt={4}>
					<Badge color="violet" variant="filled" size="md" radius="sm">
						EST. 2022
					</Badge>
				</Group>
				<Text size="lg" color="dimmed" mt={8}>The story behind Comedy Seen</Text>
			</div>

			<Space h="xl" />

			<Container size="md">

				{/* ── Two-column founder card ── */}
				<div className={classes.founderRow}>

					{/* Profile photo — circular with purple ring */}
					<div className={classes.photoCol}>
						<div className={classes.profileRing}>
							<Image
								src={profile}
								fit="cover"
								width="100%"
								height="100%"
							/>
						</div>
						<Text
							mt="sm"
							size="sm"
							color="dimmed"
							sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}
						>
							Pete · Founder
						</Text>
					</div>

					{/* Bio text */}
					<div className={classes.bioCol}>
						<Text size="md" lh={1.75}>
							Hi, I'm Pete. I love stand-up and I used to travel a lot more — and would sometimes
							find it hard to get information on mics to perform at. I remember I was performing
							at a mic in NYC and afterwards all the comics were having a hard time trying to
							figure out where to perform next.
						</Text>

						<blockquote className={classes.pullQuote}>
							"How is there not an app for that?"
						</blockquote>

						<Text size="md" lh={1.75}>
							That night was really the beginning of Comedy Seen. Then COVID hit and I decided
							to teach myself to code as a side project. I officially launched the platform in 2022
							(originally under the name Pobblebonk) — and we have been growing and supporting
							the live stand-up comedy community ever since.
						</Text>

					</div>
				</div>

				{/* ── Divider ── */}
				<Divider
					my="xl"
					label={<span className={classes.dividerLabel}>Find me IRL</span>}
					labelPosition="center"
					size="xs"
				/>

				{/* ── IRL callout card ── */}
				<div className={classes.irlCard}>
					<Group spacing="xs" mb="xs">
						<IconMapPin size={20} color="#2b9eb3" />
						<Text
							fw={700}
							sx={{ fontFamily: "'Outfit', sans-serif", color: '#1a6e7d' }}
						>
							Miami, FL
						</Text>
					</Group>
					<Text size="sm" color="dimmed" lh={1.7}>
						Occasionally you'll find me IRL at an open mic or a show in Miami.
						Come say <strong>"Hi"</strong> if you see me! I'm also a photographer,
						so you may catch me with a camera as well. 📸
					</Text>
				</div>

				<Space h="xl" />
			</Container>

			<Footer links={links} />
		</body>
	)
}

export default About;